import {
  BRIDGE_SEATS,
  type BridgeControllers,
  type BridgeSeat as Seat,
  SINGLE_PLAYER,
  clockwiseAfter,
  leftOf,
  partnerOf,
} from '../bridge/seats.js';
import { type Card, type Rank, fullDeck, sameCard, sortHand } from '../trick/cards.js';
import { seededRandom, shuffled } from '../trick/rng.js';
import { type TrickPlay, followSuit, trickWinner } from '../trick/rules.js';

export type HeartsSeat = Seat;

export class IllegalHeartsMoveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IllegalHeartsMoveError';
  }
}

/** `pass`: everyone secretly picks cards to hand on (three, or two with Big Cards). `play`: the tricks (13, or 7). `done`: the hand is over. */
export type HeartsPhase = 'pass' | 'play' | 'done';
export type PassDirection = 'left' | 'right' | 'across' | 'none';

/** Hands rotate through left, right, across and then a hand with no passing. */
export const PASS_CYCLE: readonly PassDirection[] = ['left', 'right', 'across', 'none'];
export const passDirectionFor = (handNumber: number): PassDirection => PASS_CYCLE[handNumber % PASS_CYCLE.length] as PassDirection;

export interface HeartsCompletedTrick {
  readonly leader: Seat;
  readonly plays: readonly TrickPlay<Seat>[];
  readonly winner: Seat;
}

export const TWO_OF_CLUBS: Card = { suit: 'C', rank: 2 };
export const QUEEN_OF_SPADES: Card = { suit: 'S', rank: 12 };

/**
 * What changes with the deck. Full deck: 13 cards each, pass 3, the queen is 13, a hand is 26 points, game to 100.
 * Big Cards (8 to Ace, 28 cards): 7 each, pass 2 (3 of 7 is nearly half the hand), the queen is 7 (as many as there
 * are hearts, the same ratio), a hand is 14 points, game to 50. The lowest club opens.
 */
export interface HeartsRules {
  readonly minRank: Rank;
  readonly handSize: number;
  readonly passCount: number;
  readonly queenPoints: number;
  readonly totalPoints: number;
  readonly openingCard: Card;
  readonly target: number;
}
export function heartsRules(minRank: Rank = 2): HeartsRules {
  const handSize = Math.floor(fullDeck(minRank).length / 4);
  const hearts = 15 - minRank;
  return {
    minRank,
    handSize,
    passCount: minRank >= 8 ? 2 : 3,
    queenPoints: hearts,
    totalPoints: 2 * hearts,
    openingCard: { suit: 'C', rank: minRank },
    target: minRank >= 8 ? 50 : 100,
  };
}

/** Hearts: avoid the points. Each heart is 1 point and the queen of spades is 13, so a hand is 26 points in all. */
export interface HeartsGame {
  readonly seed: number;
  /** Lowest rank in the deck: 2 full, 8 Big Cards. */
  readonly minRank: Rank;
  readonly handNumber: number;
  readonly dealer: Seat;
  readonly controllers: BridgeControllers;
  readonly hands: Readonly<Record<Seat, readonly Card[]>>;
  readonly phase: HeartsPhase;
  readonly passDirection: PassDirection;
  /** Cards each seat has chosen to pass; hidden from the others until all four are in. */
  readonly passes: Readonly<Partial<Record<Seat, readonly Card[]>>>;
  /** Cards each seat received in the pass (empty until the exchange), so a screen can mark them. */
  readonly received: Readonly<Record<Seat, readonly Card[]>>;
  readonly turn: Seat;
  readonly trick: readonly TrickPlay<Seat>[];
  readonly tricks: readonly HeartsCompletedTrick[];
  /** True once a heart has been played: only then may a trick be led with one. */
  readonly heartsBroken: boolean;
  /** Points taken so far this hand (public). */
  readonly pointsTaken: Readonly<Record<Seat, number>>;
}

export interface NewHeartsOptions {
  readonly seed: number;
  readonly minRank?: Rank;
  readonly handNumber?: number;
  readonly dealer?: Seat;
  readonly controllers?: BridgeControllers;
}

const zero = (): Record<Seat, number> => ({ N: 0, E: 0, S: 0, W: 0 });
const empty = (): Record<Seat, readonly Card[]> => ({ N: [], E: [], S: [], W: [] });

export const isPointCard = (c: Card): boolean => c.suit === 'H' || sameCard(c, QUEEN_OF_SPADES);
export const cardHeartsPoints = (c: Card, minRank: Rank = 2): number =>
  c.suit === 'H' ? 1 : sameCard(c, QUEEN_OF_SPADES) ? heartsRules(minRank).queenPoints : 0;

/** The seat that gives its cards to `seat`'s neighbour in the direction of the pass. */
export function passTarget(seat: Seat, direction: PassDirection): Seat {
  if (direction === 'left') return leftOf(seat);
  if (direction === 'across') return partnerOf(seat);
  if (direction === 'right') return leftOf(leftOf(leftOf(seat)));
  return seat;
}

const holder = (hands: Readonly<Record<Seat, readonly Card[]>>, card: Card): Seat =>
  BRIDGE_SEATS.find((s) => hands[s].some((c) => sameCard(c, card))) as Seat;

export function newHeartsGame(opts: NewHeartsOptions): HeartsGame {
  const handNumber = opts.handNumber ?? 0;
  const dealer = opts.dealer ?? 'N';
  const minRank = opts.minRank ?? 2;
  const rules = heartsRules(minRank);
  const deck = shuffled(fullDeck(minRank), seededRandom(opts.seed));
  const dealt: Record<Seat, Card[]> = { N: [], E: [], S: [], W: [] };
  const order = clockwiseAfter(dealer);
  deck.forEach((card, i) => dealt[order[i % 4] as Seat].push(card));
  const hands = Object.fromEntries(BRIDGE_SEATS.map((s) => [s, sortHand(dealt[s])])) as Record<Seat, Card[]>;
  const passDirection = passDirectionFor(handNumber);
  const noPass = passDirection === 'none';
  return {
    seed: opts.seed,
    minRank,
    handNumber,
    dealer,
    controllers: opts.controllers ?? SINGLE_PLAYER,
    hands,
    phase: noPass ? 'play' : 'pass',
    passDirection,
    passes: {},
    received: empty(),
    turn: holder(hands, rules.openingCard),
    trick: [],
    tricks: [],
    heartsBroken: false,
    pointsTaken: zero(),
  };
}

/** Seats that have not yet chosen their pass. */
export const pendingPassers = (g: HeartsGame): Seat[] => (g.phase === 'pass' ? BRIDGE_SEATS.filter((s) => g.passes[s] === undefined) : []);

/**
 * A seat's cards to pass (three, or two with Big Cards). Choices are hidden until the fourth arrives, then all four exchanges happen at once
 * and the play begins with the holder of the two of clubs.
 */
export function heartsSubmitPass(game: HeartsGame, seat: Seat, cards: readonly Card[]): HeartsGame {
  if (game.phase !== 'pass') throw new IllegalHeartsMoveError('there is no passing now');
  if (game.passes[seat] !== undefined) throw new IllegalHeartsMoveError(`${seat} has already chosen`);
  const passCount = heartsRules(game.minRank).passCount;
  if (cards.length !== passCount) throw new IllegalHeartsMoveError(`pass exactly ${passCount} cards`);
  for (const c of cards) if (!game.hands[seat].some((h) => sameCard(h, c))) throw new IllegalHeartsMoveError(`${seat} does not hold that card`);
  if (cards.some((c, i) => cards.findIndex((d) => sameCard(c, d)) !== i)) throw new IllegalHeartsMoveError(`pass ${passCount} different cards`);

  const passes = { ...game.passes, [seat]: cards };
  if (!BRIDGE_SEATS.every((s) => passes[s] !== undefined)) return { ...game, passes };

  const hands: Record<Seat, Card[]> = { N: [], E: [], S: [], W: [] };
  const received = empty() as Record<Seat, readonly Card[]>;
  for (const s of BRIDGE_SEATS) hands[s] = game.hands[s].filter((h) => !(passes[s] as readonly Card[]).some((p) => sameCard(p, h)));
  for (const s of BRIDGE_SEATS) {
    const to = passTarget(s, game.passDirection);
    hands[to] = [...hands[to], ...(passes[s] as readonly Card[])];
    received[to] = passes[s] as readonly Card[];
  }
  const sorted = Object.fromEntries(BRIDGE_SEATS.map((s) => [s, sortHand(hands[s])])) as Record<Seat, Card[]>;
  return { ...game, hands: sorted, passes, received, phase: 'play', turn: holder(sorted, heartsRules(game.minRank).openingCard) };
}

/** The cards the seat to play may play. */
export function heartsLegalPlays(game: HeartsGame): Card[] {
  if (game.phase !== 'play') return [];
  const hand = game.hands[game.turn];
  const first = game.tricks.length === 0;
  if (game.trick.length === 0) {
    if (first) return hand.filter((c) => sameCard(c, heartsRules(game.minRank).openingCard)); // the lowest club (the two, or the eight) always opens
    const nonHearts = hand.filter((c) => c.suit !== 'H');
    return game.heartsBroken || nonHearts.length === 0 ? [...hand] : nonHearts;
  }
  const follows = followSuit(hand, game.trick[0]?.card.suit);
  if (follows.length > 0 && follows.every((c) => c.suit === game.trick[0]?.card.suit)) return follows;
  // Void in the suit led: any card, except that nobody may be fed points on the very first trick unless that is all they hold.
  if (first) {
    const safe = hand.filter((c) => !isPointCard(c));
    return safe.length > 0 ? safe : [...hand];
  }
  return [...hand];
}

export function heartsPlay(game: HeartsGame, card: Card): HeartsGame {
  if (game.phase !== 'play') throw new IllegalHeartsMoveError(`cannot play a card in the ${game.phase} phase`);
  if (!heartsLegalPlays(game).some((c) => sameCard(c, card))) throw new IllegalHeartsMoveError(`${game.turn} cannot play that card now`);
  const seat = game.turn;
  const hands = { ...game.hands, [seat]: game.hands[seat].filter((c) => !sameCard(c, card)) };
  const trick = [...game.trick, { seat, card }];
  const heartsBroken = game.heartsBroken || card.suit === 'H';
  if (trick.length < 4) return { ...game, hands, trick, heartsBroken, turn: leftOf(seat) };

  const winner = trickWinner(trick, null);
  const points = trick.reduce((a, p) => a + cardHeartsPoints(p.card, game.minRank), 0);
  const tricks = [...game.tricks, { leader: (trick[0] as TrickPlay<Seat>).seat, plays: trick, winner }];
  return {
    ...game,
    hands,
    trick: [],
    tricks,
    heartsBroken,
    pointsTaken: { ...game.pointsTaken, [winner]: game.pointsTaken[winner] + points },
    turn: winner,
    phase: tricks.length === heartsRules(game.minRank).handSize ? 'done' : 'play',
  };
}

export interface HeartsHandResult {
  /** Points taken from tricks this hand. */
  readonly taken: Readonly<Record<Seat, number>>;
  /** Points added to each total after shooting the moon is applied. */
  readonly scored: Readonly<Record<Seat, number>>;
  /** The seat that took every point (26, or 14 with Big Cards): shot the moon, if any. */
  readonly moonShooter: Seat | null;
}

/** Shooting the moon (taking every heart and the queen of spades): that player adds 0 and everyone else adds the hand's total (26, or 14 with Big Cards). */
export function heartsHandResult(game: HeartsGame): HeartsHandResult | null {
  if (game.phase !== 'done') return null;
  const taken = game.pointsTaken;
  const total = heartsRules(game.minRank).totalPoints;
  const moonShooter = BRIDGE_SEATS.find((s) => taken[s] === total) ?? null;
  const scored = Object.fromEntries(
    BRIDGE_SEATS.map((s) => [s, moonShooter === null ? taken[s] : s === moonShooter ? 0 : total]),
  ) as Record<Seat, number>;
  return { taken, scored, moonShooter };
}

export const HEARTS_TARGET = 100;
/** The game target for a deck: 100 for the full deck, 50 for Big Cards. */
export const heartsTarget = (minRank: Rank = 2): number => heartsRules(minRank).target;
export type HeartsTotals = Readonly<Record<Seat, number>>;
export const NEW_HEARTS_TOTALS: HeartsTotals = { N: 0, E: 0, S: 0, W: 0 };

export const addHeartsHand = (totals: HeartsTotals, result: HeartsHandResult): HeartsTotals =>
  Object.fromEntries(BRIDGE_SEATS.map((s) => [s, totals[s] + result.scored[s]])) as Record<Seat, number>;

/**
 * Once anyone reaches the target the game ends and the LOWEST total wins. A tie for lowest plays another hand, and
 * several seats can be past the target at once.
 */
export function heartsGameWinner(totals: HeartsTotals, target = HEARTS_TARGET): Seat | null {
  if (Math.max(...BRIDGE_SEATS.map((s) => totals[s])) < target) return null;
  const low = Math.min(...BRIDGE_SEATS.map((s) => totals[s]));
  const lowest = BRIDGE_SEATS.filter((s) => totals[s] === low);
  return lowest.length === 1 ? (lowest[0] as Seat) : null;
}
